import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.tsx";
import { AppProviders } from "./providers";
import { initializeMsal } from "./config/msalConfig";
import {
  ErrorBoundary,
  ErrorFallback,
} from "./components/shared/ErrorBoundary";
import { createErrorHandler } from "./utils/errorReporting";
import { initSentry } from "./lib/sentry";

// Initialize Sentry before anything else
initSentry();

// Error handler for app-level errors
const handleAppError = createErrorHandler("App");

// Create root immediately for faster first paint
const root = createRoot(document.getElementById("root")!);

/**
 * Initial loading component - rendered immediately while MSAL initializes
 * Uses minimal inline styles to avoid CSS blocking
 * Intentionally defined inline to avoid module import overhead during initial paint
 */
// eslint-disable-next-line react-refresh/only-export-components
function InitialLoader() {
  return (
    <div
      className="initial-loader"
      role="status"
      aria-label="Laster applikasjon"
    >
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        style={{ animation: "spin 1s linear infinite", color: "#0bccda" }}
      >
        <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
        <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
      </svg>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// Render loading state immediately (before MSAL init)
// This gives us an instant first paint while MSAL initializes in background
root.render(
  <StrictMode>
    <InitialLoader />
  </StrictMode>,
);

// Initialize MSAL and then render the full app
initializeMsal().then(() => {
  root.render(
    <StrictMode>
      <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleAppError}>
        <AppProviders>
          <App />
        </AppProviders>
      </ErrorBoundary>
    </StrictMode>,
  );

  // Load web vitals after app is interactive (non-blocking)
  if (typeof requestIdleCallback !== "undefined") {
    requestIdleCallback(() => initWebVitals());
  } else {
    setTimeout(initWebVitals, 1);
  }
});

async function initWebVitals() {
  try {
    const [{ reportWebVitals }, { sendWebVitals }] = await Promise.all([
      import("./lib/reportWebVitals"),
      import("./lib/analytics/sendWebVitals"),
    ]);
    reportWebVitals(sendWebVitals);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("[web-vitals] failed to initialize reporting", error);
    }
  }
}
